import { useEffect, useState } from "react";
import { MIC_REQUIRED_FRAMES, MIC_RMS_THRESHOLD, RECORDING_SECONDS } from "../constants";
import type { UpdateCheck } from "../constants";

/**
 * Records a short audio sample from the microphone while `active`, reporting
 * progress through `update` and the outcome through `finish`. A recording
 * with no audible sound fails. The recording is kept locally for playback.
 */
export function useAudioRecordingCheck(
  active: boolean,
  micStream: MediaStream | null,
  update: UpdateCheck,
  finish: UpdateCheck,
) {
  const [micLevel, setMicLevel] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(RECORDING_SECONDS);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!active) return;
    const fail = (detail: string) => finish("recording", { status: "fail", detail });

    if (!micStream) {
      fail("Microphone access is needed to test recording");
      return;
    }
    const Recorder = (window as any).MediaRecorder;
    if (!Recorder) {
      fail("Your browser does not support recording");
      return;
    }

    let recorder: any;
    const chunks: Blob[] = [];
    try {
      recorder = new Recorder(micStream);
    } catch {
      fail("Could not start a recording on this device");
      return;
    }

    // Measure the input level while recording so a silent microphone is caught
    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 1024;
    audioContext.createMediaStreamSource(micStream).connect(analyser);
    const samples = new Float32Array(analyser.fftSize);
    let loudFrames = 0;
    let rafId = 0;
    const measure = () => {
      analyser.getFloatTimeDomainData(samples);
      let sum = 0;
      for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
      const rms = Math.sqrt(sum / samples.length);
      setMicLevel(rms);
      if (rms > MIC_RMS_THRESHOLD) loudFrames++;
      rafId = requestAnimationFrame(measure);
    };
    rafId = requestAnimationFrame(measure);

    recorder.ondataavailable = (event: any) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    recorder.onerror = () => {
      recorder.onstop = null;
      fail("Recording failed on this device");
    };
    recorder.onstop = () => {
      cancelAnimationFrame(rafId);
      setMicLevel(0);
      const blob = new Blob(chunks, { type: recorder.mimeType });
      if (!blob.size) {
        fail("Nothing was recorded");
        return;
      }
      if (loudFrames < MIC_REQUIRED_FRAMES) {
        fail("No sound was picked up. Check that your microphone is not muted and run the test again.");
        return;
      }
      setPlaybackUrl(URL.createObjectURL(blob));
      finish("recording", {
        status: "pass",
        detail: "Audio recorded. Play it back to confirm you can hear yourself.",
      });
    };

    update("recording", {
      status: "running",
      detail: `Recording for ${RECORDING_SECONDS} seconds — speak now`,
    });
    setSecondsLeft(RECORDING_SECONDS);
    recorder.start();
    let remaining = RECORDING_SECONDS;
    const intervalId = setInterval(() => {
      remaining -= 1;
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(intervalId);
        if (recorder.state !== "inactive") recorder.stop();
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
      cancelAnimationFrame(rafId);
      audioContext.close();
      recorder.onstop = null;
      if (recorder.state !== "inactive") recorder.stop();
    };
  }, [active, micStream, update, finish]);

  useEffect(() => {
    return () => {
      if (playbackUrl) URL.revokeObjectURL(playbackUrl);
    };
  }, [playbackUrl]);

  return { micLevel, secondsLeft, playbackUrl };
}
